import { observer } from 'mobx-react-lite';
import { SidePanelWidth } from '@wix/bex-core';
import { useWixPatternsContainer, usePageContext } from '@wix/bex-core/react';
import { SlidingModal } from '../SlidingModal';
import React, { useEffect, useMemo } from 'react';
import { reaction } from 'mobx';
import { SidePanelModalItem } from './SidePanelModalItem';
import { SidePanelsState } from '../../state/SidePanelsState';

export interface SidePanelModalProps {
  top: number;
  state: SidePanelsState;
}

function _SidePanelModal(props: SidePanelModalProps) {
  const { top, state } = props;
  const { onSidePanelOpen, closeSidePanel } = useWixPatternsContainer();
  const pageState = usePageContext();

  const { sidePanel, sidePanelComponents } = state;

  const { isOpen } = sidePanel;

  const style = useMemo(
    () => ({
      top,
      bottom: 0,
      overflow: 'hidden',
      width: SidePanelWidth,
    }),
    [top],
  );

  useEffect(() => {
    return reaction(
      () => sidePanel.isOpen,
      (sidePanelOpen) => {
        pageState.sidePanelOpen = sidePanelOpen;
      },
    );
  }, []);

  useEffect(() => {
    const onGlobalSidePanelOpenSub = onSidePanelOpen?.(() => {
      state.closeSidePanel();
    });

    const disposers = [
      reaction(
        () => sidePanel.isOpen,
        (sidePanelShow) => {
          if (sidePanelShow) {
            closeSidePanel?.();
          }
        },
      ),
    ];

    return () => {
      onGlobalSidePanelOpenSub?.remove();
      disposers.forEach((d) => d?.());
    };
  }, []);

  return (
    <SlidingModal
      dataHook="cairo-side-panel-modal"
      transitionStatus={sidePanel.transition.status}
      style={style}
      contentRef={(el) => {
        sidePanel.focus.modalElement = el;
      }}
      onExited={() => {
        sidePanel.transition.setStatus('exited');
      }}
      onEntered={() => {
        sidePanel.transition.setStatus('entered');
      }}
      width={SidePanelWidth}
      isOpen={isOpen}
      onRequestClose={() => {
        sidePanel.close();
      }}
    >
      {Array.from(sidePanelComponents.entries()).map(([key, ComponentType]) => {
        return (
          <SidePanelModalItem
            key={key}
            id={key}
            width={SidePanelWidth}
            state={state}
          >
            <ComponentType />
          </SidePanelModalItem>
        );
      })}
    </SlidingModal>
  );
}

export const SidePanelModal = observer(_SidePanelModal);
